import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Link2, Check, Users, MapPin, Tag, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/Layout";
import { CommunitySignup } from "@/components/CommunitySignup";
import { BookingModal } from "@/components/BookingModal";
import { LocationMap, Timetable, photoUrl } from "@/components/LocationParts";
import { api, trackEvent } from "@/lib/apiClient";

function ShareButton() {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ url }); return; } catch { return; } }
    await navigator.clipboard.writeText(url);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button data-testid="location-share" onClick={share} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-ice hover:text-cyan transition-colors">
      {copied ? <Check size={15} /> : <Link2 size={15} />} {copied ? "Link copied" : "Share this location"}
    </button>
  );
}

export default function BookLocation() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [locations, setLocations] = useState(null);
  const [modal, setModal] = useState({ open: false });
  const [showUpsell, setShowUpsell] = useState(false);
  const [availability, setAvailability] = useState({});
  const loadAvailability = () => api.get(`/locations/${locationId}/availability`).then((r) => setAvailability(r.data)).catch(() => {});

  useEffect(() => {
    trackEvent("book_start", { location: locationId });
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
    loadAvailability();
  }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loc = locations?.find((l) => l.id === locationId);
  const img = photoUrl(loc);
  const closeModal = () => { setModal({ open: false }); loadAvailability(); setShowUpsell(true); setTimeout(() => setShowUpsell(false), 8000); };

  return (
    <Layout page="book">
      <section className="relative pt-32 sm:pt-36 pb-10 overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-[1100px] px-5 sm:px-8">
          <Link to="/book" data-testid="location-back" className="inline-flex items-center gap-2 text-sm text-ice/70 hover:text-cyan transition-colors"><ArrowLeft size={15} /> All locations</Link>
          {locations && !loc ? (
            <div className="py-20" data-testid="location-not-found">
              <h1 className="font-display text-4xl font-bold text-white">Location not found</h1>
              <p className="text-ice/70 mt-3">This location may have moved or isn't open for booking yet.</p>
            </div>
          ) : loc ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="label text-cyan mt-8 block">Book at</span>
              <h1 data-testid="location-title" className="font-display font-black text-white tracking-tighter mt-3 leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">{loc.name}</h1>
              <p className="text-ice/80 text-base sm:text-lg mt-3">{loc.studio}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-mist">
                {loc.address && <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-cyan" /> {loc.address}</span>}
                {loc.capacity && <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-cyan" /> Up to {loc.capacity} per class</span>}
              </div>
              {loc.schedule_note && <p className="text-cyan/90 text-sm mt-3">{loc.schedule_note}</p>}
              <div className="mt-6"><ShareButton /></div>
            </motion.div>
          ) : <div className="h-64" />}
        </div>
      </section>

      {loc && (
        <section className="relative pb-20">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-8 space-y-6">
            {img && <img src={img} alt={`${loc.studio || loc.name}`} data-testid="location-photo" className="w-full aspect-[16/9] sm:aspect-[21/9] object-cover rounded-2xl border border-white/8" />}
            <div className="rounded-2xl border border-white/8 bg-ink-800 overflow-hidden" data-testid="location-timetable">
              <div className="px-5 sm:px-6 py-4 border-b border-white/8"><p className="label text-cyan">Timetable</p></div>
              <Timetable loc={loc} availability={availability} onBook={(slot, location) => setModal({ open: true, mode: "single", slot, location })} />
            </div>
            <LocationMap loc={loc} />
          </div>
        </section>
      )}

      <section className="relative pb-28">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-8"><CommunitySignup locations={locations || []} /></div>
      </section>

      <BookingModal open={modal.open} onClose={closeModal} mode={modal.mode} slot={modal.slot} location={modal.location} dates={modal.slot ? availability[modal.slot.id] : undefined} onSoldOut={loadAvailability} />

      <AnimatePresence>
        {showUpsell && (
          <motion.button data-testid="bundle-upsell" onClick={() => navigate("/book?tab=bundles")}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[140] flex items-center gap-3 rounded-full glass px-6 py-3.5 text-ice hover:text-white glow-blue whitespace-nowrap">
            <Tag size={16} className="text-cyan" /> Save with a bundle <ArrowRight size={16} />
          </motion.button>
        )}
      </AnimatePresence>
    </Layout>
  );
}
